import {
  baseballLogicService,
  DataGrid,
  DefaultContextMenuOptionsBuilder,
  Dexterity,
  IBulkPlayerEditorMetadata,
  IDataGridColumnConfiguration,
  IDataGridValidator,
  IPlayer,
  RightLeft,
} from '@trackman/baseball-shared-components';
import { ServiceTypes } from 'constants/ServiceTypes';
import { ColumnName } from 'enums/ColumnName';
import { HandednessFields } from 'enums/HandednessFields';
import { Ruleset } from 'enums/Ruleset';
import { observer } from 'mobx-react-lite';
import { ReviewGame } from 'models/review/ReviewGame';
import React, { useEffect, useState } from 'react';
import { emptyApiPlayLineup, emptyCustomMetadata } from 'services/helper-services/constants';
import { playerToReviewPlayerMap } from 'services/helper-services/mappingHelpers/playerMappings';
import { useInjection } from 'services/ioc/ioc.react';
import { DataKey } from '../../../enums/DataKey';
import { IApiPlayWithBallFinals } from '../../../interfaces/session/IApiPlayWithBallFinals';
import { ReviewDataRow } from '../../../models/review/ReviewDataRow/ReviewDataRow';
import { SessionReviewRowManager } from '../../../models/review/SessionReviewRowManager';
import { allValidators, basicValidators } from '../../../services/helper-services/reviewValidators/AllValidators';
import { noteResolvedValidator } from '../../../services/helper-services/reviewValidators/NoteResolvedValidator';
import { GameReviewStore } from '../../../stores/GameReviewStore';
import { StatusCell } from './CellComponents/StatusCell/StatusCell';
import { EditPlaysGridWrapper } from './EditPlaysGridStyles';
import PlayEditor from './ModalEditors/PlayEditor/PlayEditor';
import {
  PlayerTrackingColumnConfigurations,
  SessionReviewColumnConfigurations,
  SessionReviewColumnConfigurationsWithPlayerTracking,
} from './SessionReviewColumnConfigurations';

interface IProps {
  session: ReviewGame;
  rowManager: SessionReviewRowManager;
}
const EditPlaysGrid: React.FC<IProps> = observer((props): React.ReactElement => {
  const { session, rowManager } = props;

  const STICKY_COLUMN_WIDTH = 173;

  const reviewStore = useInjection<GameReviewStore>(ServiceTypes.GameReviewStore);
  const [isPlayEditorVisible, setIsPlayEditorVisible] = useState<boolean>(false);

  const createEmptyRow = () => {
    const apiPlay = {
      play: {
        ...baseballLogicService.createEmptyApiPlay(session.sessionId),
        customMetadata: { ...emptyCustomMetadata },
        playLineup: { ...emptyApiPlayLineup },
      },
      ballFinals: [],
      isChanged: true,
    } as IApiPlayWithBallFinals;
    return new ReviewDataRow(apiPlay, (session.sessionMetadata.homeTeam?.name ?? ''), (session.sessionMetadata.awayTeam?.name ?? ''));
  };
  const addRowAbove = () => {
    const newRow = createEmptyRow();
    rowManager.selection && rowManager.insertRowAtPosition(newRow, rowManager.selection.rowId);
    rowManager.updateGrid();
  };
  const addRowBelow = () => {
    const newRow = createEmptyRow();
    rowManager.selection && rowManager.insertRowAtPosition(newRow, rowManager.selection.rowId, true);
    rowManager.updateGrid();
  };
  const deleteRows = () => {
    rowManager.selection &&
      rowManager.deleteRows(rowManager.selection.rowId, rowManager.selection.toRowIndex - rowManager.selection.fromRowIndex + 1);
    rowManager.updateGrid();
  };

  const getValidators = (): IDataGridValidator[] => {
    if (session.sessionMetadata.ruleSet === Ruleset.scrimmage) {
      return [noteResolvedValidator];
    }

    if (session.sessionMetadata.isPlayerTrackingEnabled) {
      return allValidators;
    }

    return basicValidators;
  };

  const getColumns = (): IDataGridColumnConfiguration[] => {
    if (session.sessionMetadata.isPlayerTrackingEnabled) {
      return [...SessionReviewColumnConfigurationsWithPlayerTracking, ...PlayerTrackingColumnConfigurations];
    }

    return SessionReviewColumnConfigurations;
  };

  const removeValue = () => {
    if (rowManager.selection) {
      for (let i = rowManager.selection.fromRowIndex; i <= rowManager.selection.toRowIndex; i++) {
        rowManager.updateRow(i, rowManager.columns[rowManager.selection.columnIndex].dataKey, undefined);
      }
      rowManager.updateGrid();
    }
  };

  const setDexterityOnPlayerChange = (selectedRows: ReviewDataRow[], value: any, column: any): void => {
    selectedRows.forEach((selectedRow) => {
      const pitchingHandedness = value.pitchingHandedness;
      const battingHandedness = value.battingHandedness;

      if (column.dataKey === DataKey.Pitcher) {
        if (pitchingHandedness === Dexterity.Both) {
          if (selectedRow.batterDexterity === Dexterity.Right) {
            rowManager.updateRow(selectedRow.displayIndex, DataKey.PitcherDexterity, Dexterity.Left);
          } else {
            rowManager.updateRow(selectedRow.displayIndex, DataKey.PitcherDexterity, Dexterity.Right);
          }
        } else {
          rowManager.updateRow(selectedRow.displayIndex, DataKey.PitcherDexterity, value.pitchingHandedness);
        }
      }

      if (column.dataKey === DataKey.Batter) {
        if (battingHandedness === Dexterity.Both) {
          if (selectedRow.pitcherDexterity === Dexterity.Right) {
            rowManager.updateRow(selectedRow.displayIndex, DataKey.BatterDexterity, Dexterity.Left);
          } else {
            rowManager.updateRow(selectedRow.displayIndex, DataKey.BatterDexterity, Dexterity.Right);
          }
        } else {
          rowManager.updateRow(selectedRow.displayIndex, DataKey.BatterDexterity, value.battingHandedness);
        }
      }

      rowManager.updateGrid();
    });
  };

  useEffect(() => {
    if (session.plays) {
      rowManager.parseRows(reviewStore.reviewGame);
      rowManager.setColumns(getColumns());
      rowManager.contextMenuOptionsBuilder = new DefaultContextMenuOptionsBuilder(
        {
          addRowAbove,
          addRowBelow,
          deleteRows,
          editFullRow: () => {
            setIsPlayEditorVisible(true);
          },
          editValue: () => rowManager.showBulkEditor(),
          removeValue: () => removeValue(),
        },
        [DataKey.FirstBase, DataKey.SecondBase, DataKey.ThirdBase]
      );
      rowManager.getMetadataForBulkEditor = (col: IDataGridColumnConfiguration, rows: ReviewDataRow[]) => {
        if (rows.length === 0) {
          return;
        }
        const isHomeTeamBatting = baseballLogicService.isHomeTeamBatting(rows[0].inning.isTopInning);
        switch (col.dataKey) {
          case DataKey.Pitcher:
            return {
              players: isHomeTeamBatting ? session.awayTeamPlayers : session.homeTeamPlayers,
              playerToOutputModelMap: (player: IPlayer | undefined) =>
                playerToReviewPlayerMap(
                  player,
                  isHomeTeamBatting ? (session.sessionMetadata.awayTeam?.name ?? '') : (session.sessionMetadata.homeTeam?.name ?? '')
                ),
              allowAddingPlaceholderPlayers: true,
              allowAddindForeignIdToPlaceholderPlayer: session.sessionMetadata.privateSession,
              handednessFields: [HandednessFields.PITCHING],
              handednessOptions: RightLeft,
              additionalHandler: setDexterityOnPlayerChange,
            } as IBulkPlayerEditorMetadata;

          case DataKey.Catcher:
          case DataKey.FirstBaseman:
          case DataKey.SecondBaseman:
          case DataKey.ThirdBaseman:
          case DataKey.ShortStopper:
          case DataKey.LeftFielder:
          case DataKey.CenterFielder:
          case DataKey.RightFielder:
            return {
              players: isHomeTeamBatting ? session.awayTeamPlayers : session.homeTeamPlayers,
              playerToOutputModelMap: (player: IPlayer | undefined) =>
                playerToReviewPlayerMap(
                  player,
                  isHomeTeamBatting ? (session.sessionMetadata.awayTeam?.name ?? '') : (session.sessionMetadata.homeTeam?.name ?? '')
                ),
              allowAddingPlaceholderPlayers: true,
              allowAddindForeignIdToPlaceholderPlayer: session.sessionMetadata.privateSession,
            } as IBulkPlayerEditorMetadata;

          case DataKey.Batter: {
            return {
              players: isHomeTeamBatting ? session.homeTeamPlayers : session.awayTeamPlayers,
              playerToOutputModelMap: (player: IPlayer | undefined) =>
                playerToReviewPlayerMap(
                  player,
                  isHomeTeamBatting ? (session.sessionMetadata.homeTeam?.name ?? '') : (session.sessionMetadata.awayTeam?.name ?? '')
                ),
              allowAddingPlaceholderPlayers: true,
              allowAddindForeignIdToPlaceholderPlayer: session.sessionMetadata.privateSession,
              handednessFields: [HandednessFields.BATTING],
              handednessOptions: RightLeft,
              additionalHandler: setDexterityOnPlayerChange,
            } as IBulkPlayerEditorMetadata;
          }

          case DataKey.FirstBase:
          case DataKey.SecondBase:
          case DataKey.ThirdBase:
            return {
              players: isHomeTeamBatting ? session.homeTeamPlayers : session.awayTeamPlayers,
              playerToOutputModelMap: (player: IPlayer | undefined) =>
                playerToReviewPlayerMap(
                  player,
                  isHomeTeamBatting ? (session.sessionMetadata.homeTeam?.name ?? '') : (session.sessionMetadata.awayTeam?.name ?? '')
                ),
              allowAddingPlaceholderPlayers: true,
              allowAddindForeignIdToPlaceholderPlayer: session.sessionMetadata.privateSession,
            } as IBulkPlayerEditorMetadata;
        }
      };
      rowManager.getSubtitleForHeader = (column: IDataGridColumnConfiguration): React.ReactElement | undefined => {
        switch (column.columnName) {
          case ColumnName.Note: {
            const notes = rowManager.rows.filter((r) => !!r.note.note).length;
            const resolvedNotes = rowManager.rows.filter((r) => !!r.note && r.note.isResolved).length;
            return (
              <div>
                {notes} notes ({resolvedNotes} Resolved)
              </div>
            );
          }
          case ColumnName.Pitcher:
          case ColumnName.Batter: {
            return <div>Team</div>;
          }
        }
        return undefined;
      };

      rowManager.validationService.validators = getValidators();
      rowManager.validateRows();
      rowManager.setLivePlays();
      rowManager.updateGrid();
    } else {
      rowManager.setRows([]);
    }
  }, [session]);

  const stickyLeftColumnRenderer = (key: string, row: ReviewDataRow, rowIndex: number, style: any): React.ReactElement => {
    return (
      <StatusCell
        key={key}
        style={style}
        cellContent={row.status}
        isWarmup={row.isWarmup}
        convertPitchCallback={() => handleConvertPitch(row, rowIndex)}
        isHidden={row.isHidden()}
      />
    );
  };

  const handleConvertPitch = (row: ReviewDataRow, rowIndex: number) => {
    rowManager.updateRow(rowIndex, DataKey.IsWarmup, !row.isWarmup);
  };
  return (
    <EditPlaysGridWrapper>
      <PlayEditor
        visible={isPlayEditorVisible}
        onDismissCallback={() => {
          setIsPlayEditorVisible(false);
          rowManager.updateGrid();
        }}
        rows={rowManager.rows}
        selection={rowManager.selection}
        updateRow={(index: number, dataKey: string, newValue: any) => rowManager.updateRow(index, dataKey, newValue)}
        sessionMetadata={reviewStore.reviewGame.sessionMetadata}
      />
      <DataGrid
        rowManager={rowManager}
        stickyColumn={{
          width: STICKY_COLUMN_WIDTH,
          cellRenderer: stickyLeftColumnRenderer,
        }}
        arrowsScrollControl
      />
    </EditPlaysGridWrapper>
  );
});

export default EditPlaysGrid;
